import { useState } from 'react';
import { FormikProps, FormikValues } from 'formik';
import { useOrbitAccountLookup } from './hooks/useOrbitAccountLookup';
import { ToastSwal } from '../../components/general/toastSwals/toastSwal';
import { AccountAddress } from '../../../shared/types/modules/account';
import { OrbitCallInfoValues } from './callInfo/formData';
import { OrbitChannelFormValues } from './channelForm/formData';
import { OrbitCitizenInfoValues } from './citizenInfo/formData';
import { OrbitPqrInfoValues } from './pqrInfo/formData';
import { OrbitPrivacyConsentValues } from './privacyConsent/formData';
import { OrbitSpecialConditionValues } from './specialCondition/formData';
import { buildOrbitCasePayload } from './utils/buildCasePayload';
import { asyncCreateOrbitCase } from '../../../services/orbit.service';
import { extractOrbitApiErrorMessage } from '../../../shared/utils/orbitApiError';
import {
    OrbitCaseCreateResponse,
    OrbitCaseCreated,
} from '../../../shared/types/orbit';

const resolveOrbitFilingNumber = (
    res: OrbitCaseCreateResponse | undefined
): string => {
    const orbitPayload = res?.orbit as
        | OrbitCaseCreated
        | Record<string, unknown>
        | undefined;
    const orbitItems = (orbitPayload as { items?: OrbitCaseCreated['data'] })
        ?.items;

    return (
        res?.integration?.orbitCaseNumber ||
        res?.pqr?.consecutiveFormat ||
        (orbitPayload as OrbitCaseCreated | undefined)?.data?.case_number ||
        orbitItems?.case_number ||
        ''
    );
};

const validateSection = async <T extends FormikValues>(
    formik?: FormikProps<T>
): Promise<boolean> => {
    if (!formik) return false;

    const errors = await formik.validateForm();
    const isValid = Object.keys(errors).length === 0;

    if (!isValid) {
        formik.setTouched(
            Object.keys(formik.initialValues).reduce(
                (acc, key) => ({ ...acc, [key]: true }),
                {}
            )
        );
        formik.setErrors(errors);
    }

    return isValid;
};

export const useOrbitRadicationForm = () => {
    const [loading, setLoading] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [filingNumber, setFilingNumber] = useState('');
    const [formikOrbitChannel, setFormikOrbitChannel] =
        useState<FormikProps<OrbitChannelFormValues>>();
    const [formikCallInfo, setFormikCallInfo] =
        useState<FormikProps<OrbitCallInfoValues>>();
    const [formikCitizenInfo, setFormikCitizenInfo] =
        useState<FormikProps<OrbitCitizenInfoValues>>();
    const [formikAddress, setFormikAddress] =
        useState<FormikProps<AccountAddress>>();
    const [formikSpecialCondition, setFormikSpecialCondition] =
        useState<FormikProps<OrbitSpecialConditionValues>>();
    const [formikPqrInfo, setFormikPqrInfo] =
        useState<FormikProps<OrbitPqrInfoValues>>();
    const [formikPrivacyConsent, setFormikPrivacyConsent] =
        useState<FormikProps<OrbitPrivacyConsentValues>>();
    const [attachmentsResetKey, setAttachmentsResetKey] = useState(0);

    const {
        lookingUpAccount,
        lookupAccountByDocument,
        resetAccountLookup,
        catalogsReady,
    } = useOrbitAccountLookup({
        formikCitizenInfo,
        formikAddress,
        formikSpecialCondition,
    });

    const handleSubmit = async () => {
        const [
            callInfoValid,
            channelValid,
            citizenValid,
            addressValid,
            specialConditionValid,
            pqrValid,
            privacyConsentValid,
        ] = await Promise.all([
            validateSection(formikCallInfo),
            validateSection(formikOrbitChannel),
            validateSection(formikCitizenInfo),
            validateSection(formikAddress),
            validateSection(formikSpecialCondition),
            validateSection(formikPqrInfo),
            validateSection(formikPrivacyConsent),
        ]);

        if (
            !callInfoValid ||
            !channelValid ||
            !citizenValid ||
            !addressValid ||
            !specialConditionValid ||
            !pqrValid ||
            !privacyConsentValid
        ) {
            return;
        }

        setLoading(true);

        let payload;

        try {
            payload = await buildOrbitCasePayload({
                callInfo: formikCallInfo!.values,
                channel: formikOrbitChannel!.values,
                citizen: formikCitizenInfo!.values,
                address: formikAddress!.values,
                specialCondition: formikSpecialCondition!.values,
                pqrInfo: formikPqrInfo!.values,
                privacyConsent: formikPrivacyConsent!.values,
            });
        } catch (error) {
            ToastSwal.fire({
                text:
                    error instanceof Error
                        ? error.message
                        : 'No se pudo preparar el caso para Orbit. Verifica los datos e intenta nuevamente.',
                icon: 'error',
                timer: 9000,
                position: 'center-end',
            });
            setLoading(false);
            return;
        }

        let res;
        let requestErrorMessage: string | undefined;

        try {
            res = await asyncCreateOrbitCase(payload);
        } catch (error) {
            requestErrorMessage = extractOrbitApiErrorMessage(
                error as Record<string, unknown>
            );
        }

        const integrationStatus = res?.integration?.status;
        const orbitFilingNumber = resolveOrbitFilingNumber(res);
        const orbitErrorMessage =
            requestErrorMessage ||
            extractOrbitApiErrorMessage(
                res as Record<string, unknown> | undefined
            );

        const attachmentsErrorMessage = extractOrbitApiErrorMessage(
            res?.integration?.attachmentsError as
                | Record<string, unknown>
                | undefined
        );
        const attachmentsExpected = res?.integration?.attachmentsExpected ?? 0;
        const attachmentsUploaded = res?.integration?.attachmentsUploaded ?? 0;
        const hasPendingAttachments =
            attachmentsExpected > 0 &&
            attachmentsUploaded < attachmentsExpected;

        if (integrationStatus === 'SUCCESS' && orbitFilingNumber) {
            if (attachmentsErrorMessage || hasPendingAttachments) {
                ToastSwal.fire({
                    text: attachmentsErrorMessage
                        ? `El caso se creó en Orbit, pero no se pudieron subir todos los adjuntos: ${attachmentsErrorMessage}`
                        : `El caso se creó en Orbit, pero solo se subieron ${attachmentsUploaded} de ${attachmentsExpected} adjuntos.`,
                    icon: 'warning',
                    timer: 9000,
                    position: 'center-end',
                });
            }
            setFilingNumber(orbitFilingNumber);
            setSuccessOpen(true);
        } else if (integrationStatus === 'SUCCESS' && !orbitFilingNumber) {
            ToastSwal.fire({
                text: 'El caso se creó en Orbit, pero no se obtuvo el número de radicado.',
                icon: 'warning',
                timer: 9000,
                position: 'center-end',
            });
        } else {
            ToastSwal.fire({
                text:
                    orbitErrorMessage ||
                    'Lo sentimos, no se pudo crear el caso en Orbit. Por favor, verifica los datos e intenta nuevamente.',
                icon: 'error',
                timer: orbitErrorMessage ? 9000 : 5000,
                position: 'center-end',
            });
        }

        setLoading(false);
    };

    const handleClean = () => {
        formikCallInfo?.resetForm();
        formikOrbitChannel?.resetForm();
        formikCitizenInfo?.resetForm();
        formikAddress?.resetForm();
        formikSpecialCondition?.resetForm();
        formikPqrInfo?.resetForm();
        formikPrivacyConsent?.resetForm();
        setAttachmentsResetKey((key) => key + 1);
        resetAccountLookup();
    };

    const handleNewRequest = () => {
        setSuccessOpen(false);
        setFilingNumber('');
        handleClean();
    };

    const handleRefreshPage = () => {
        window.location.reload();
    };

    return {
        loading,
        successOpen,
        filingNumber,
        attachmentsResetKey,
        lookingUpAccount,
        lookupAccountByDocument,
        catalogsReady,
        handleSubmit,
        handleClean,
        handleNewRequest,
        handleRefreshPage,
        formikOrbitChannel,
        setFormikOrbitChannel,
        setFormikCallInfo,
        setFormikCitizenInfo,
        setFormikAddress,
        setFormikSpecialCondition,
        setFormikPqrInfo,
        setFormikPrivacyConsent,
    };
};
